import {View} from 'react-native';

import React from 'react';
import {Props} from './model';
import {Routes, Screens} from '../../shared/routing';
import {Breadcrumbs} from '../../shared/components/breadcrumbs';
import Screen from '../../shared/components/screen';
import {useStyles} from '../../shared/hooks/styles';
import {DeviceSize} from '../../shared/theme/helpers';
import {SocialSupportMeasure, SSMFormNames} from '../../shared/models/api';
import {CheckmarkText} from '../../shared/components/checkmarkText';
import {PrivilegeDetailsParameter} from './conponents/parameter';
import {SsmResult} from './conponents/ssmResult';
import {Text} from '../../shared/components/text';
import {CustomScrollView} from '../../shared/components/scrollView';
import {LoadableContent} from '../../shared/components/loadableContent';
import {useIsDesktop} from '../../shared/hooks/useIsDesktop';
import {EvenOddList} from '../../shared/components/evenOddList';

interface DetailsParam {
  name: string;
  value: string | number | null | undefined;
  renderValue?: string;
  isImportant?: boolean;
}

const getRoutes = (privilege: SocialSupportMeasure) => [
  {
    title: 'Возможности карты',
    routeName: Screens.CardServices,
    webRoute: Routes.cardService,
  },
  {
    title: 'Мои льготы',
    routeName: Screens.PrivilegesService,
    webRoute: Routes.privilegesService,
  },
  {
    title: privilege.name || 'Льгота',
    routeName: Screens.PrivilegesServiceDetails,
    webRoute: Routes.privilegesServiceDetails,
  },
];

export default ({privilege}: Props) => {
  const isDesktop = useIsDesktop();
  const styles = useStyles(({theme, mediaOnly}) => ({
    sectionContainer: {
      marginTop: 32,
      paddingHorizontal: 24,
      paddingBottom: theme.screen.bottomOffset,
      flex: 1,
    },
    sectionContent: {
      ...mediaOnly(DeviceSize.desktop, {
        marginTop: 40,
      }),
    },
    sectionTitle: {
      ...mediaOnly(
        DeviceSize.desktop,
        {...theme.fonts.h5Light, marginBottom: 40},
        {...theme.fonts.bodySmallLight, marginBottom: 30},
      ),
    },
    parameters: {
      ...mediaOnly(DeviceSize.desktop, {
        flexDirection: 'row',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
      }),
    },
    ssmResult: {
      ...mediaOnly(
        DeviceSize.desktop,
        {
          marginTop: 35,
        },
        {marginTop: 30},
      ),
    },
    parametersItem: {
      ...mediaOnly(
        DeviceSize.desktop,
        {
          width: '50%',
          paddingRight: 15,
          marginBottom: 30,
        },
        {marginBottom: 10},
      ),
    },
    prop: {
      textAlign: 'right',
      ...theme.fonts.body,
      color: theme.color.textColor,
      opacity: 1,
      flex: 1,
    },
    importantProp: {
      textAlign: 'right',
      ...theme.fonts.action,
      color: theme.color.textColor,
      opacity: 1,
      flex: 1,
    },
    label: {
      ...theme.fonts.bodySmallLight,
      color: theme.color.labelText,
      flex: 1.4,
    },
  }));

  const {ssmForm, assignedSSM, period, regulatoryLevelName} = privilege ?? {};

  const paramList: DetailsParam[] = [
    {
      name: 'Форма предоставления',
      value: (ssmForm && SSMFormNames[ssmForm]) || ssmForm,
    },
    {
      name: 'Объем назначения',
      value: assignedSSM?.volume,
      renderValue: `${assignedSSM?.volume} руб.`,
    },
    {
      name: 'Дата назначения',
      value: assignedSSM?.dateSoc,
    },
    {
      name: 'Срок предоставления',
      value: assignedSSM?.startDate,
      renderValue:
        assignedSSM?.startDate && assignedSSM?.endDate
          ? `${assignedSSM?.startDate} - ${assignedSSM?.endDate}`
          : `${assignedSSM?.startDate}`,
    },
    {
      name: 'Периодичность',
      value: period,
    },
    {
      name: 'Уровень нормативно-правового регулирования',
      value: regulatoryLevelName,
    },
  ];

  const renderParamDesktop = ({value, name, renderValue}: DetailsParam) => {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    return (
      <View style={styles.parametersItem}>
        <PrivilegeDetailsParameter title={name}>
          <CheckmarkText text={renderValue || value.toString() || ''} />
        </PrivilegeDetailsParameter>
      </View>
    );
  };
  const renderParamMobile = ({value, name, renderValue, isImportant}: DetailsParam): {value: JSX.Element} | null => {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    return {
      value: (
        <>
          <Text style={styles.label}>{name}:</Text>
          <Text style={isImportant ? styles.importantProp : styles.prop}>{renderValue || value.toString() || ''}</Text>
        </>
      ),
    };
  };
  const renderParamList = () => {
    if (isDesktop) {
      return paramList.map(renderParamDesktop);
    } else {
      return (
        <EvenOddList
          testIDPrefix='privilieges-details'
          list={paramList.map(renderParamMobile).filter((p) => p !== null) as any}
        />
      );
    }
  };

  return (
    <Screen>
      <CustomScrollView contentInsetAdjustmentBehavior='never'>
        <LoadableContent loading={!privilege}>
          {!!privilege && (
            <View style={styles.sectionContainer}>
              <Breadcrumbs routes={getRoutes(privilege)} />
              <View style={styles.sectionContent}>
                <Text style={styles.sectionTitle}>{privilege.name}</Text>
                <View style={styles.parameters}>{renderParamList()}</View>
                {assignedSSM?.sign && (
                  <View style={styles.ssmResult}>
                    <SsmResult text='Льгота предоставлена в текущем периоде' />
                  </View>
                )}
              </View>
            </View>
          )}
        </LoadableContent>
      </CustomScrollView>
    </Screen>
  );
};
